import {
  TopicProps,
  TopicsGroup,
} from '@/components/ConversationWithTutor/MessageItem/MessageItem';
import { TutorChatLevel, TutorChatStyle } from '@mavatar/melingo-voice-and-avatar-sdk';
import { TutorInformation } from '@/types/unit-data';
import { UserState, LevelSettingItem } from '@/types/user';
import { unitDataService } from '@/services/unit-data';

export enum QuizStatus {
  NONE = 'NONE',
  PRACTICE = 'PRACTICE',
  IMPROVEMENT = 'IMPROVEMENT',
  COMPLETED = 'COMPLETED',
}

if (!(window as any).tutor_audio) {
  (window as any).tutor_audio = new Audio(
    'data:audio/wav;base64,UklGRigAAABXQVZFZm10IBAAAAABAAEARKwAAIhYAQACABAAZGF0YQQAAAAAAAAD'
  );
}

const TutorTopics = {
  sortAscending: (keyA: any, keyB: any) => {
    return keyA < keyB ? -1 : keyA > keyB ? 1 : 0;
  },

  sortDescending: (keyA: any, keyB: any) => {
    return keyA < keyB ? 1 : keyA > keyB ? -1 : 0;
  },

  normalizeTutorInformation: (tutorInformation?: TutorInformation | string) => {
    return tutorInformation && typeof tutorInformation === 'string'
      ? (JSON.parse(tutorInformation) as TutorInformation)
      : (tutorInformation as TutorInformation | undefined);
  },

  tutorInformationToTopic: (
    tutorInformation: TutorInformation,
    ellLevel: number,
    ellUnit: number,
    ellTopic?: number,
    ellActivity?: number
  ) => {
    const topic: TopicProps = {
      style: tutorInformation.style,
      level: ellLevel
        ? TutorTopics.ellLevelToTutorChatLevel(Number(ellLevel))
        : tutorInformation.level,
      text: tutorInformation.topic,
      topic: tutorInformation.topic,
      user_role: tutorInformation.user_role,
      system_role: tutorInformation.system_role,
      extraField: {
        ellLevel: `${ellLevel}`,
        ellUnit: `${ellUnit}`,
        ellTopic: ellTopic || ellTopic === 0 ? `${ellTopic}` : undefined,
        ellActivity: ellActivity || ellActivity === 0 ? `${ellActivity}` : undefined,
      },
    };

    return topic;
  },

  ellLevelToTutorChatLevel: (ellLevel: number) => {
    let level: TutorChatLevel = TutorChatLevel.A1;

    switch (ellLevel) {
      case 0:
      case 1:
      case 2:
        level = TutorChatLevel.A1;
        break;
      case 3:
        level = TutorChatLevel.A2;
        break;
      case 4:
        level = TutorChatLevel.B1;
        break;
      case 5:
        level = TutorChatLevel.B2;
        break;
      case 6:
        level = TutorChatLevel.C1;
        break;
      case 7:
        level = TutorChatLevel.C2;
        break;
      default:
        level = TutorChatLevel.A1;
        break;
    }

    return level;
  },

  TutorChatLevelToEllLevel: (tutorChatLevel: TutorChatLevel) => {
    let level: number = 0;

    switch (tutorChatLevel) {
      case TutorChatLevel.A1:
        level = 1;
        break;
      case TutorChatLevel.A2:
        level = 3;
        break;
      case TutorChatLevel.B1:
        level = 4;
        break;
      case TutorChatLevel.B2:
        level = 5;
        break;
      case TutorChatLevel.C1:
        level = 6;
        break;
      case TutorChatLevel.C2:
        level = 7;
        break;
    }

    return level;
  },

  createDefaultTopic: (
    tipoc: string,
    ellLevel: number | string,
    ellUnit: number,
    ellTopic?: number,
    ellActivity?: number
  ) => {
    const level: TutorChatLevel = TutorTopics.ellLevelToTutorChatLevel(Number(ellLevel));

    const topic: TopicProps = {
      style: TutorChatStyle.SMALLTALK,
      level: level,
      text: tipoc,
      topic: tipoc,
      user_role:
        'Be engaged, answer your questions, ask questions or make comments about the topic',
      system_role:
        'You are a polite tutor that tries to engage the user with smalltalk about the topic',
      extraField: {
        ellLevel: `${ellLevel}`,
        ellUnit: `${ellUnit}`,
        ellTopic: ellTopic || ellTopic === 0 ? `${ellTopic}` : undefined,
        ellActivity: ellActivity || ellActivity === 0 ? `${ellActivity}` : undefined,
      },
    };

    return topic;
  },

  getLevelUnits: async (user: UserState | null, level: number) => {
    const levelUnits = ((await unitDataService.fetchLevelUnits(user, level))?.data?.data || [])
      .sort((unitA, unitB) => TutorTopics.sortAscending(unitA.order, unitB.order))
      .map(unit => {
        return {
          ...unit,
          tutorInformation: TutorTopics.normalizeTutorInformation(unit.tutorInformation),
        };
      });
    return levelUnits;
  },

  getUnitTopics: async (user: UserState | null, level: number, unitId: number) => {
    const unitTopics = (
      (await unitDataService.fetchUnitTopics(user, level, unitId))?.data?.data || []
    )
      .sort((topicA, topicB) => TutorTopics.sortAscending(topicA.order, topicB.order))
      .map(topic => {
        return {
          ...topic,
          tutorInformation: TutorTopics.normalizeTutorInformation(topic.tutorInformation),
        };
      });
    return unitTopics;
  },

  getTopicActivities: async (
    user: UserState | null,
    level: number,
    unitId: number,
    topicId: number
  ) => {
    const topicActivities = (
      (await unitDataService.fetchTopicActivities(user, level, unitId, topicId))?.data?.data || []
    )
      .sort((activityA, activityB) => TutorTopics.sortAscending(activityA.order, activityB.order))
      .map(activity => {
        return {
          ...activity,
          tutorInformation: TutorTopics.normalizeTutorInformation(activity.tutorInformation),
        };
      });
    return topicActivities;
  },

  getAllTutorTopics: async (
    user: UserState | null,
    levels: LevelSettingItem[],
    abortSignal?: AbortSignal
  ) => {
    const topicsGroups: TopicsGroup[] = [];

    const isAborted = () => abortSignal && abortSignal.aborted;

    for (let levelIndex: number = 0; levelIndex < levels.length; levelIndex++) {
      if (isAborted()) {
        return;
      }

      const lavelData = levels[levelIndex];

      const topicsOfLefel: TopicsGroup = {
        title: lavelData.name,
        topics: [],
      };

      const levelUnits = await TutorTopics.getLevelUnits(user, lavelData.id);

      for (let unitsIndex: number = 0; unitsIndex < levelUnits.length; unitsIndex++) {
        if (isAborted()) {
          return;
        }

        const unit = levelUnits[unitsIndex];

        let topicsOfUnit: TopicProps[] = [
          unit.tutorInformation
            ? TutorTopics.tutorInformationToTopic(unit.tutorInformation, lavelData.id, unit.id)
            : TutorTopics.createDefaultTopic(unit.name, lavelData.id, unit.id),
        ];

        const unitTopics = await TutorTopics.getUnitTopics(user, lavelData.id, unit.id);

        for (let topicIndex: number = 0; topicIndex < unitTopics.length; topicIndex++) {
          if (isAborted()) {
            return;
          }

          const topic = unitTopics[topicIndex];

          const topicActivities = await TutorTopics.getTopicActivities(
            user,
            lavelData.id,
            unit.id,
            topic.id
          );
          const topicActivitiesTutorInformation = topicActivities.map(activity => {
            return activity.tutorInformation
              ? TutorTopics.tutorInformationToTopic(
                  activity.tutorInformation,
                  lavelData.id,
                  unit.id,
                  topic.id,
                  activity.id
                )
              : TutorTopics.createDefaultTopic(
                  activity.title,
                  lavelData.id,
                  unit.id,
                  topic.id,
                  activity.id
                );
          });

          const topicsOfEllTopic: TopicProps[] = [
            topic.tutorInformation
              ? TutorTopics.tutorInformationToTopic(
                  topic.tutorInformation,
                  lavelData.id,
                  unit.id,
                  topic.id
                )
              : TutorTopics.createDefaultTopic(topic.name, lavelData.id, unit.id, topic.id),
            ...topicActivitiesTutorInformation,
          ];

          topicsOfUnit = [...topicsOfUnit, ...topicsOfEllTopic];
        }

        topicsOfLefel.topics = [...topicsOfLefel.topics, ...topicsOfUnit];
      }

      if (topicsOfLefel.topics.length > 0) {
        topicsGroups.push(topicsOfLefel);
      }
    }

    return topicsGroups;
  },

  getTutorTopicsForAllTopicsInLastUsersUnit: async (
    user: UserState | null,
    levels: LevelSettingItem[],
    abortSignal?: AbortSignal
  ) => {
    const topicsGroups: TopicsGroup[] = [];

    const isAborted = () => abortSignal && abortSignal.aborted;

    const topics: TopicsGroup = {
      topics: [],
    };
    const currentLevel = user?.progress?.userLevel || levels[0].id;
    const currentLevelData = levels.find(level => level.id === currentLevel);

    if (currentLevelData) {
      const levelUnits = await TutorTopics.getLevelUnits(user, currentLevelData.id);

      if (isAborted()) {
        return;
      }

      const currentUnitIndex = user?.progress?.completedUnits || 0;
      const currentUnitData =
        levelUnits.length > currentUnitIndex
          ? levelUnits[currentUnitIndex]
          : levelUnits.length > 0
            ? levelUnits[levelUnits.length - 1]
            : null;

      if (currentUnitData) {
        const unitTopics = await TutorTopics.getUnitTopics(
          user,
          currentLevelData.id,
          currentUnitData.id
        );

        if (isAborted()) {
          return;
        }

        topics.topics = unitTopics.map(topic => {
          if (topic.tutorInformation) {
            return TutorTopics.tutorInformationToTopic(
              topic.tutorInformation,
              currentLevelData.id,
              currentUnitData.id,
              topic.id
            );
          } else {
            return TutorTopics.createDefaultTopic(
              topic.name,
              currentLevelData.id,
              currentUnitData.id,
              topic.id
            );
          }
        });
      }
    }

    if (topics.topics.length > 0) {
      topicsGroups.push(topics);
    }

    return topicsGroups;
  },

  getTutorTopicsForLastUsersActivity: async (
    user: UserState | null,
    levels: LevelSettingItem[],
    abortSignal?: AbortSignal
  ) => {
    const topicsGroups: TopicsGroup[] = [];

    const isAborted = () => abortSignal && abortSignal.aborted;

    const topics: TopicsGroup = {
      topics: [],
    };
    const currentLevel = user?.progress?.userLevel || levels[0].id;
    const currentLevelData = levels.find(level => level.id === currentLevel);

    if (currentLevelData) {
      const levelUnits = await TutorTopics.getLevelUnits(user, currentLevelData.id);

      if (isAborted()) {
        return;
      }

      const currentUnitIndex = user?.progress?.completedUnits || 0;
      const currentUnitData =
        levelUnits.length > currentUnitIndex
          ? levelUnits[currentUnitIndex]
          : levelUnits.length > 0
            ? levelUnits[levelUnits.length - 1]
            : null;

      if (currentUnitData) {
        if (currentUnitData.tutorInformation) {
          topics.topics.push(
            TutorTopics.tutorInformationToTopic(
              currentUnitData.tutorInformation,
              currentLevelData.id,
              currentUnitData.id
            )
          );
        } else {
          topics.topics.push(
            TutorTopics.createDefaultTopic(
              currentUnitData.name,
              currentLevelData.id,
              currentUnitData.id
            )
          );
        }

        const unitTopics = await TutorTopics.getUnitTopics(
          user,
          currentLevelData.id,
          currentUnitData.id
        );

        if (isAborted()) {
          return;
        }

        const notComplitedTopics = unitTopics.filter(
          topic =>
            !user?.progress?.unit?.topicsOverviewPerUnit?.[currentUnitData.id]?.[topic.id]
              ?.isAllActivitiesCompleted
        );
        const currentTopicData =
          notComplitedTopics.length > 0
            ? notComplitedTopics[0]
            : unitTopics.length > 0
              ? unitTopics[unitTopics.length - 1]
              : null;

        if (currentTopicData) {
          if (currentTopicData.tutorInformation) {
            topics.topics.push(
              TutorTopics.tutorInformationToTopic(
                currentTopicData.tutorInformation,
                currentLevelData.id,
                currentUnitData.id,
                currentTopicData.id
              )
            );
          } else {
            topics.topics.push(
              TutorTopics.createDefaultTopic(
                currentTopicData.name,
                currentLevelData.id,
                currentUnitData.id,
                currentTopicData.id
              )
            );
          }

          const topicActivities = await TutorTopics.getTopicActivities(
            user,
            currentLevelData.id,
            currentUnitData.id,
            currentTopicData.id
          );

          if (isAborted()) {
            return;
          }

          const notComplitedActivities = topicActivities.filter(
            activity =>
              !user?.progress?.topic?.activitiesOverviewPerTopic?.[currentTopicData.id]?.[
                activity.id
              ]
          );
          const currentActivityData =
            notComplitedActivities.length > 0
              ? notComplitedActivities[0]
              : topicActivities.length > 0
                ? topicActivities[topicActivities.length - 1]
                : null;

          if (currentActivityData) {
            if (currentActivityData.tutorInformation) {
              topics.topics.push(
                TutorTopics.tutorInformationToTopic(
                  currentActivityData.tutorInformation,
                  currentLevelData.id,
                  currentUnitData.id,
                  currentTopicData.id,
                  currentActivityData.id
                )
              );
            } else {
              topics.topics.push(
                TutorTopics.createDefaultTopic(
                  currentActivityData.title,
                  currentLevelData.id,
                  currentUnitData.id,
                  currentTopicData.id,
                  currentActivityData.id
                )
              );
            }
          }
        }
      }
    }

    if (topics.topics.length > 0) {
      topicsGroups.push(topics);
    }

    return topicsGroups;
  },

  getTutorTopicsForSpecificEllUnit: async (
    user: UserState | null,
    level: number,
    unitId: number
  ) => {
    const levelUnits = await TutorTopics.getLevelUnits(user, level);
    const unit = levelUnits.find(unit => unit.id === unitId);

    if (unit?.tutorInformation) {
      return TutorTopics.tutorInformationToTopic(unit.tutorInformation, level, unit.id);
    }

    if (unit) {
      return TutorTopics.createDefaultTopic(unit.name, level, unit.id);
    }

    return null;
  },

  getTutorTopicsForSpecificEllTopic: async (
    user: UserState | null,
    level: number,
    unitId: number,
    topicId: number
  ) => {
    const levelTopics = await TutorTopics.getUnitTopics(user, level, unitId);
    const topic = levelTopics.find(topic => topic.id === topicId);

    if (topic?.tutorInformation) {
      return TutorTopics.tutorInformationToTopic(topic.tutorInformation, level, unitId, topic.id);
    }

    if (topic) {
      return TutorTopics.createDefaultTopic(topic.name, level, unitId, topic.id);
    }

    return null;
  },

  getTutorTopicsForSpecificEllActivity: async (
    user: UserState | null,
    level: number,
    unitId: number,
    topicId: number,
    activityId: number
  ) => {
    const levelActivities = await TutorTopics.getTopicActivities(user, level, unitId, topicId);
    const activity = levelActivities.find(activity => activity.id === activityId);

    if (activity?.tutorInformation) {
      return TutorTopics.tutorInformationToTopic(
        activity.tutorInformation,
        level,
        unitId,
        topicId,
        activity.id
      );
    }

    if (activity) {
      return TutorTopics.createDefaultTopic(activity.title, level, unitId, topicId, activity.id);
    }

    return null;
  },

  getQuizStatus: (score?: number | null) => {
    if (typeof score !== 'number') {
      return QuizStatus.NONE;
    }

    return score < 70
      ? QuizStatus.PRACTICE
      : score < 100
        ? QuizStatus.IMPROVEMENT
        : QuizStatus.COMPLETED;
  },

  getFromScoreOfQuizStatus: (status: QuizStatus) => {
    switch (status) {
      case QuizStatus.NONE:
        return 0;
      case QuizStatus.PRACTICE:
        return 0;
      case QuizStatus.IMPROVEMENT:
        return 70;
      case QuizStatus.COMPLETED:
        return 100;
    }
  },

  getToScoreOfQuizStatus: (status: QuizStatus) => {
    switch (status) {
      case QuizStatus.NONE:
        return 100;
      case QuizStatus.PRACTICE:
        return 69;
      case QuizStatus.IMPROVEMENT:
        return 99;
      case QuizStatus.COMPLETED:
        return 100;
    }
  },

  unlockAutoPlayAudioOption: () => {
    // unlock auto play audio option
    const audio = (window as any).tutor_audio as HTMLAudioElement;
    audio.src =
      'data:audio/wav;base64,UklGRigAAABXQVZFZm10IBAAAAABAAEARKwAAIhYAQACABAAZGF0YQQAAAAAAAAD';
    audio
      .play()
      .then(() => console.log('Audio context unlocked successfully!'))
      .catch(err => console.log('Unlock failed', err));
  },

  getGlobalAudio: () => {
    return (window as any).tutor_audio as HTMLAudioElement;
  },
};

export default TutorTopics;
